import type { Tutorial } from '../../shared/types.js';

/**
 * Contextual tour of the agent detail screen (the page you land on after
 * launching/opening an agent). It runs in place via the page banner shown on
 * `/agents/:id`, so its steps declare no `path` and anchor to elements that
 * only exist on that screen. See `pageTutorials.ts` for the route match and
 * `TutorialLauncher` for how it behaves when started from the Tutorials list.
 */
export const agentDetailTutorial: Tutorial = {
  name: 'agent-detail',
  title: 'Work with a running agent',
  description:
    'A quick tour of the agent workspace: the live chat stream, sending follow-ups (and stopping the agent), the view tabs plugins extend, the run details panel, and the actions menu.',
  category: 'agent-sessions',
  docPages: ['concepts/agents'],
  contextual: true,
  agenda: [
    'Follow the live stream',
    'Send follow-ups and attach files',
    'Review the changes and run details',
    'Control the run',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Your agent workspace',
      body: "Now that your agent is running, this is where you follow its work and steer it. Here's a quick lap around the screen.",
    },
    {
      id: 'chat-log',
      kind: 'info',
      title: 'The live stream',
      body: 'Watch the agent think and act in real time here: messages, tool calls, and file edits stream in as they happen. Scroll up any time to revisit earlier steps.',
      target: '[data-tutorial-id="agent-chat-log"]',
      placement: 'right',
    },
    {
      id: 'chat-input',
      kind: 'info',
      title: 'Send follow-ups',
      body: 'Type here to give the agent more instructions or answer its questions. Your message is queued even while it is mid-task.',
      target: '[data-tutorial-id="agent-chat-input"]',
      placement: 'top',
    },
    {
      id: 'attach-images',
      kind: 'info',
      title: 'Paste or attach files',
      body: 'Paste a screenshot straight from your clipboard, drag a file onto the box, or use the paperclip to attach images and other files for the agent to look at.',
      target: '[aria-label="Attach file"]',
      placement: 'top',
    },
    {
      id: 'stop-agent',
      kind: 'info',
      title: 'Stop the agent',
      body: 'While the agent is working, this button interrupts it; handy when it is heading the wrong way and you want to redirect before it finishes.',
      target: '[aria-label="Interrupt agent"]',
      placement: 'top',
    },
    {
      id: 'tabs',
      kind: 'info',
      title: 'Switch views',
      body: 'These tabs change what this panel shows: the live **Chat**, the diff **Review**, and any extra views your installed plugins contribute. Plugins register their own tabs here, so this row grows as you add capabilities.',
      target: '[aria-label="Agent view"]',
      placement: 'bottom',
    },
    {
      id: 'review-tab',
      kind: 'info',
      title: 'Review the changes',
      body: "Switch to the Review tab to see the agent's proposed code changes as a diff. Read through them, leave comments, and decide what to keep before anything ships.",
      target: '[data-tutorial-id="agent-tab-code-review"]',
      placement: 'bottom',
    },
    {
      id: 'details',
      kind: 'info',
      title: 'Run details',
      body: 'Open **Details** from the rail on the right for everything about this run: model, blueprint, devbox, environment, and attachments - in a side panel, without leaving the stream.',
      target: '[aria-label="Details"]',
      placement: 'bottom',
    },
    {
      id: 'actions-menu',
      kind: 'action',
      trigger: 'click',
      title: 'More actions',
      body: 'The rest of the controls live in this menu in the top-right. Open it to see them - the tour continues once it is open.',
      target: '[data-tutorial-id="agent-actions-menu"]',
      placement: 'bottom',
      // The menu is a Radix DropdownMenu: it opens on pointerdown and the
      // portaled content immediately covers the trigger, so no `click` ever
      // lands on it for the engine to observe - advance the moment the menu
      // content appears instead (matches the agent-type/model Select steps).
      advanceWhen: '[data-tutorial-id="agent-actions-menu-content"]',
    },
    {
      id: 'actions-menu-items',
      kind: 'info',
      title: "What's in the menu",
      body: '**Launch again** re-runs the task in a fresh agent, **Save as persona** stores its setup so you can reuse it, and **Delete** removes the run for good. Sharing has its own button beside this menu.',
      target: '[data-tutorial-id="agent-actions-menu-content"]',
      placement: 'left',
    },
    {
      id: 'done',
      kind: 'info',
      title: "That's the tour",
      body: "You're all set. Keep an eye on the stream, send follow-ups whenever you need to, and stop the agent if it heads the wrong way. Curious what its devbox is doing under the hood? The sandbox monitor tour is next.",
    },
  ],
  // Next stop after the chat workspace: the sandbox monitor. That tour launches
  // its own short CPU-test run so the box stays active and its live charts move.
  handoff: { tutorialName: 'sandbox-monitor', label: 'Monitor the agent sandbox' },
};
